<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const profile = ref(null);
const hasChanges = ref(false);

const form = ref({
  about: '',
  description: '',
  email: '',
  address: '',
  website1: '',
  website2: '',
  vertical: '',
});

const VERTICALS = [
  { value: '', label: '' },
  { value: 'AUTOMOTIVE', label: 'Automotive' },
  { value: 'BEAUTY', label: 'Beauty' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'EVENT_PLANNING', label: 'Event Planning' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'NONPROFIT', label: 'Nonprofit' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'TECH', label: 'Tech' },
  { value: 'OTHER', label: 'Other' },
];

const aboutCount = computed(() => (form.value.about || '').length);
const descriptionCount = computed(() => (form.value.description || '').length);

const profilePictureUrl = computed(() => profile.value?.profile_picture_url);

const fetchProfile = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getBusinessProfile(props.inbox.id);
    profile.value = data;
    form.value.about = data.about || '';
    form.value.description = data.description || '';
    form.value.email = data.email || '';
    form.value.address = data.address || '';
    form.value.website1 = data.websites?.[0] || '';
    form.value.website2 = data.websites?.[1] || '';
    form.value.vertical = data.vertical || '';
    hasChanges.value = false;
  } catch {
    useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.ALERTS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const markChanged = () => {
  hasChanges.value = true;
};

const saveProfile = async () => {
  isSaving.value = true;
  try {
    const websites = [form.value.website1, form.value.website2].filter(
      w => w && w.trim()
    );
    await InboxesAPI.updateBusinessProfile(props.inbox.id, {
      about: form.value.about,
      description: form.value.description,
      email: form.value.email,
      address: form.value.address,
      websites,
      vertical: form.value.vertical || undefined,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.ALERTS.SAVED'));
    hasChanges.value = false;
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_PROFILE.ALERTS.SAVE_ERROR');
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchProfile);
</script>

<template>
  <div class="mx-8 flex flex-col gap-6">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
    </div>

    <template v-else>
      <!-- Profile Picture -->
      <SettingsSectionCard
        :title="t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.TITLE')"
        :description="t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.DESCRIPTION')"
      >
        <div class="flex items-center gap-4">
          <div
            class="size-20 rounded-full bg-n-solid-3 border border-n-weak flex items-center justify-center overflow-hidden shrink-0"
          >
            <img
              v-if="profilePictureUrl"
              :src="profilePictureUrl"
              class="size-full object-cover"
              :alt="t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.TITLE')"
            />
            <span v-else class="i-lucide-building-2 size-8 text-n-slate-8" />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.HINT') }}
            </span>
          </div>
        </div>
      </SettingsSectionCard>

      <!-- Profile Fields -->
      <SettingsSectionCard
        :title="t('INBOX_MGMT.WHATSAPP_PROFILE.INFO.TITLE')"
        :description="t('INBOX_MGMT.WHATSAPP_PROFILE.INFO.DESCRIPTION')"
      >
        <div class="flex flex-col gap-4">
          <!-- About -->
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ABOUT') }}
              </label>
              <span class="text-xs text-n-slate-9">
                {{ `${aboutCount}/139` }}
              </span>
            </div>
            <input
              v-model="form.about"
              type="text"
              maxlength="139"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
              :placeholder="t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.ABOUT')"
              @input="markChanged"
            />
          </div>

          <!-- Description -->
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.DESCRIPTION') }}
              </label>
              <span class="text-xs text-n-slate-9">
                {{ `${descriptionCount}/512` }}
              </span>
            </div>
            <textarea
              v-model="form.description"
              maxlength="512"
              rows="3"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand resize-none"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.DESCRIPTION')
              "
              @input="markChanged"
            />
          </div>

          <!-- Email -->
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL') }}
            </label>
            <input
              v-model="form.email"
              type="email"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
              :placeholder="t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.EMAIL')"
              @input="markChanged"
            />
          </div>

          <!-- Address -->
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ADDRESS') }}
            </label>
            <input
              v-model="form.address"
              type="text"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.ADDRESS')
              "
              @input="markChanged"
            />
          </div>

          <!-- Websites -->
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.WEBSITES') }}
            </label>
            <input
              v-model="form.website1"
              type="url"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.WEBSITE')
              "
              @input="markChanged"
            />
            <input
              v-model="form.website2"
              type="url"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.PLACEHOLDERS.WEBSITE_2')
              "
              @input="markChanged"
            />
            <span class="text-xs text-n-slate-9">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.HINTS.WEBSITES') }}
            </span>
          </div>

          <!-- Vertical / Category -->
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.VERTICAL') }}
            </label>
            <select
              v-model="form.vertical"
              class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand cursor-pointer"
              @change="markChanged"
            >
              <option v-for="v in VERTICALS" :key="v.value" :value="v.value">
                {{ v.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Save -->
        <div
          class="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-n-weak"
        >
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_PROFILE.SAVE')"
            icon="i-lucide-check"
            blue
            :is-loading="isSaving"
            :disabled="!hasChanges"
            @click="saveProfile"
          />
        </div>
      </SettingsSectionCard>
    </template>
  </div>
</template>
